import React, { useEffect, useState } from 'react';
import StatCard from '../../components/charts/StatCard';
import BarChart from '../../components/charts/BarChart';
import DataTable from '../../components/tables/DataTable';
import ticketService from '../../services/ticketService';
import { formatDate, formatTimeAgo } from '../../utils/dateFormatter';
import { STATUS_COLORS, PRIORITY_COLORS } from '../../constants/statuses';

const ManagerOverview = ({ navigateToTickets, setCurrentPage, navigateToEngineers }) => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [ticketsResult, statsResult] = await Promise.all([
      ticketService.getAllTickets(),
      ticketService.getDashboardStats()
    ]);
    
    if (ticketsResult.success) {
      setTickets(ticketsResult.data);
    }
    if (statsResult.success) {
      setStats(statsResult.data);
    }
    setLoading(false);
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const chartGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

   const recentTickets = [...tickets]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 10);

  const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => `#${value}`
    },
    {
      key: 'title',
      label: 'Title'
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${PRIORITY_COLORS[value]}20`,
          color: PRIORITY_COLORS[value]
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${STATUS_COLORS[value]}20`,
          color: STATUS_COLORS[value]
        }}>
          {value.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => formatTimeAgo(value)
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading dashboard...
      </div>
    );
  }

  return (
    <div>
      <div style={gridStyle}>
        <StatCard
          title="Total Tickets"
          value={stats?.total_tickets || 0}
          icon=""
          subtitle="All time"
          color="#3b82f6"
          onClick={() => navigateToTickets('')}
        />
        <StatCard
          title="Open Tickets"
          value={stats?.open_tickets || 0}
          icon=""
          subtitle="Active tickets"
          color="#f59e0b"
          onClick={() => navigateToTickets('OPEN')}
        />
        <StatCard
          title="Closed Today"
          value={stats?.closed_today || 0}
          icon=""
          subtitle="Closed today"
          color="#10b981"
          onClick={() => navigateToTickets('CLOSED')}
        />
        <StatCard
          title="Critical"
          value={stats?.critical_tickets || 0}
          icon=""
          subtitle="Needs attention"
          color="#ef4444"
          onClick={() => navigateToTickets('', 'CRITICAL', true)}
        />
      </div>

      <div style={gridStyle}>
        <StatCard
          title="Overdue Tickets"
          value={stats?.overdue_tickets || 0}
          icon=""
          subtitle="Past SLA"
          color="#dc2626"
          onClick={() => navigateToTickets('OVERDUE')}
        />
        <StatCard
          title="Active Engineers"
          value={stats?.active_engineers || 0}
          icon=""
          subtitle="Currently working"
          color="#8b5cf6"
          onClick={() => navigateToEngineers('WORKING')}
        />
        <StatCard
          title="Avg Response Time"
          value={stats?.avg_response_time || 'N/A'}
          icon=""
          subtitle="Response period"
          color="#06b6d4"
        />
        <StatCard
          title="Satisfaction"
          value={stats?.customer_satisfaction ? `${stats.customer_satisfaction}%` : 'N/A'}
          icon=""
          subtitle="Customer rating"
          color="#f59e0b"
        />
      </div>

      <div style={chartGridStyle}>
        <BarChart
          data={stats?.daily_trend || []}
          title="Daily Ticket Trend (This Week)"
          xKey="day"
          yKey="created"
          color="var(--primary)"
          height={280}
        />
        <BarChart
          data={stats?.daily_trend || []}
          title="Tickets Closed (This Week)"
          xKey="day"
          yKey="resolved"
          color="#10b981"
          height={280}
        />
      </div>

      <div style={{ marginTop: '32px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px' }}>
          Recent Tickets
        </h2>
        <DataTable
          columns={columns}
          data={recentTickets}
          actions={[]}
        />
      </div>
    </div>
  );
};

export default ManagerOverview;
